import * as THREE from 'three';
import { hexToWorld, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { ShakeFx } from './neck.ts';
import { captureSky, paintHour, type Sky } from './shore.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Order's post (El Párpado 1, docs/SAGAS.md), at night on the titan's head. The board's grass goes to the old
 * grey-green hide of the head, creased, no trees; round the node, the post's ruins: low broken stone walls in a ring
 * (open where the roads come in, a tower stump and a torn red banner of the Order either side), a cold hearth, and by
 * the node the huge empty wooden frame the bell hung from, a chain hanging off its beam. The camp going up: two tents
 * of dark oilcloth and the third still only its poles. Beyond the board, where the camera looks, the eye: a closed
 * eyelid the size of a valley, scaled, its seam a faint line of light that now and then brightens.
 *
 * Draw calls: the ruins and camp (one merged mesh), the eyelid (one), its seam (one), a glow layer. No lights: the
 * night is the board's own lights repainted (as La Orilla's nights), the camp's warmth glow sprites.
 */

const S = WORLD_PER_HEX;
const HIDE = [new THREE.Color('#58624f'), new THREE.Color('#4f5947'), new THREE.Color('#5f6a55')];
const HIDE_ROAD = new THREE.Color('#6e7462');
const STONE = ['#6e6c68', '#5e5c58', '#7a7772'];
const MOSS = '#3e4a36';
const WOOD = ['#3e3024', '#4a3a2c', '#33281e'];
const IRON = '#26262a';
const CLOTH = ['#3a4654', '#33404c', '#2e3842'];
const BANNER = '#8a2a22';
const HIDE_HEX = ['#4f5946', '#47503f', '#56604b'];
const LID = '#4c5644';
const LID_DARK = '#2a3026';
const WARM = new THREE.Color('#ffb070');
const SLIT = new THREE.Color('#ffe8c0');

/** Where the bell's frame stands: by the node at the post; at the slope's top (past the far end of the second road) on the climb. */
export const frameSpot = (map: MapDef, mode: 'post' | 'slope', winches: { x: number; y: number }[] = []): { x: number; z: number; a: number; H: number } => {
  const mouths = map.sectors.map((sec) => {
    const g = hexToWorld(sec.gate.q, sec.gate.r);
    return Math.atan2(g.y, g.x);
  });
  if (mode === 'slope') {
    // The escort's road ends at the far end of the second road: the frame waits a hex further out.
    const sec = map.sectors[1] ?? map.sectors[0];
    const end = sec?.path[0] ?? { q: map.radius, r: 0 };
    const w = hexToWorld(end.q, end.r);
    const a = Math.atan2(w.y, w.x);
    const R = (Math.hypot(w.x, w.y) / 1000) * S + 1.1 * S;
    return { x: Math.cos(a) * R, z: Math.sin(a) * R, a, H: FRAME_H };
  }
  const f0 = mouths[0] ?? 0;
  const gap = (a: number, from: number[]): number => Math.min(...from.map((m) => Math.abs(Math.atan2(Math.sin(a - m), Math.cos(a - m)))));
  let a: number;
  if (winches.length) {
    // Hanging the bell: the frame stands where it is furthest from the roads' mouths and the winches round it.
    const away = [...mouths, ...winches.map((w) => Math.atan2(w.y, w.x))];
    a = f0;
    let best = -1;
    for (let k = 0; k < 72; k++) {
      const t = (k / 72) * Math.PI * 2;
      const g = gap(t, away);
      if (g > best) {
        best = g;
        a = t;
      }
    }
  } else {
    const side = gap(f0 + Math.PI / 2, mouths) >= gap(f0 - Math.PI / 2, mouths) ? 1 : -1;
    a = f0 + (side * Math.PI) / 2;
  }
  return { x: Math.cos(a) * 1.45 * S, z: Math.sin(a) * 1.45 * S, a, H: FRAME_H };
};

/** The frame's posts' height (its beam on top; the yoke hangs under it). */
export const FRAME_H = 3.1;

export class OrderPostLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(80);
  private fx: ShakeFx;
  private sky: Sky;
  private camp: THREE.Vector3[] = [];
  private eye = new THREE.Vector3();
  private seamM: THREE.MeshBasicMaterial;
  private now = 0;

  /**
   * `post`: the Order's ruined post round the node (El Párpado 1's night, and hanging the bell in 2). `slope`: the
   * head's slope the bell is dragged up (El Párpado 2): no ruins or camp, the hide's folds and the empty frame waiting at
   * the top. `night`: the board's light repainted to a moonlit night.
   */
  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    mode: 'post' | 'slope' = 'post',
    night = true,
    winches: { x: number; y: number }[] = [],
  ) {
    this.group.name = 'order-post';
    this.fx = new ShakeFx(ctx);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const cone = new THREE.ConeGeometry(0.5, 1, 4);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const link = new THREE.TorusGeometry(0.07, 0.022, 4, 8);
    const pot = new THREE.SphereGeometry(0.5, 8, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2);
    const kit = new Kit();
    // Where the roads come in (by angle): the ruins stand open there.
    const mouths = map.sectors.map((sec) => {
      const g = hexToWorld(sec.gate.q, sec.gate.r);
      return Math.atan2(g.y, g.x);
    });
    const nearMouth = (a: number, w: number): boolean => mouths.some((m) => Math.abs(Math.atan2(Math.sin(a - m), Math.cos(a - m))) < w);
    // The fight opens looking out from the node to the first road: the frame stands to one side of that look (the
    // side further from the other roads), the hearth to the other, the camp behind the node (low, under the view).
    const f0 = mouths[0] ?? 0;
    const gap = (a: number): number => Math.min(...mouths.map((m) => Math.abs(Math.atan2(Math.sin(a - m), Math.cos(a - m)))));
    const side = gap(f0 + Math.PI / 2) >= gap(f0 - Math.PI / 2) ? 1 : -1;
    const spot = frameSpot(map, mode, winches);
    // Nothing of the ruins or the camp stands on a winch (hanging the bell): a hex round each is kept clear.
    const ws = winches.map((w) => [(w.x / 1000) * S, (w.y / 1000) * S] as const);
    const clear = (x: number, z: number): boolean => !ws.some(([wx, wz]) => Math.hypot(x - wx, z - wz) < 1.5);
    const frameAt = spot.a;
    const hearthAt = f0 - (side * Math.PI) / 2;
    const campAt = f0 + Math.PI;

    // ---- The ruins: low broken walls in a ring, two and a half hexes out, open at the roads.
    const post = mode === 'post';
    const ringR = 2.55 * S;
    const n = 30;
    const seg = (Math.PI * 2 * ringR) / n;
    for (let i = 0; i < (post ? n : 0); i++) {
      const a = (i / n) * Math.PI * 2;
      if (nearMouth(a, 0.4)) continue;
      const x = Math.cos(a) * ringR;
      const z = Math.sin(a) * ringR;
      if (!clear(x, z)) continue;
      const g = ctx.ground(x, z);
      // Broken: some stretches stand to the knee, some to the waist, some are a heap of stones.
      const h = [0.28, 0.5, 0.18, 0.62, 0.36][i % 5] as number;
      // Along the ring (a box's length is its x).
      const yaw = -a - Math.PI / 2;
      if (i % 7 === 3) {
        for (let k = 0; k < 3; k++) kit.add(rock, STONE[k % 3] as string, place(x + Math.cos(a + k) * 0.18, g + 0.07, z + Math.sin(a + k) * 0.18, k + i, 0.2));
        continue;
      }
      kit.add(box, STONE[i % 3] as string, place(x, g + h / 2, z, yaw, new THREE.Vector3(seg + 0.03, h, 0.32)));
      kit.add(box, STONE[(i + 1) % 3] as string, place(x, g + h + 0.03, z, yaw + 0.04, new THREE.Vector3(seg - 0.05, 0.06, 0.36)));
      if (i % 3 === 0) kit.add(box, MOSS, place(x, g + h + 0.065, z, yaw, new THREE.Vector3(0.18, 0.02, 0.3)));
      if (i % 4 === 1) kit.add(rock, STONE[2] as string, place(x + Math.cos(a) * 0.35, g + 0.06, z + Math.sin(a) * 0.35, i, 0.16));
    }
    // Tower stumps either side of each opening, a torn banner of the Order on a pole by each.
    for (const m of post ? mouths : []) {
      for (const side of [-1, 1]) {
        const a = m + side * 0.46;
        const x = Math.cos(a) * ringR;
        const z = Math.sin(a) * ringR;
        if (!clear(x, z)) continue;
        const g = ctx.ground(x, z);
        kit.add(cyl, STONE[0] as string, place(x, g + 0.45, z, a, new THREE.Vector3(0.5, 0.9, 0.5)));
        kit.add(cyl, STONE[1] as string, place(x, g + 0.94, z, a, new THREE.Vector3(0.56, 0.08, 0.56)));
        const ox = Math.cos(a + Math.PI / 2);
        const oz = Math.sin(a + Math.PI / 2);
        kit.add(box, WOOD[0] as string, place(x, g + 1.4, z, 0, new THREE.Vector3(0.05, 1.0, 0.05)));
        kit.add(box, BANNER, place(x + ox * 0.16, g + 1.6, z + oz * 0.16, -a - Math.PI / 2, new THREE.Vector3(0.3, 0.5, 0.02), 0, side * 0.08));
        kit.add(box, BANNER, place(x + ox * 0.2, g + 1.27, z + oz * 0.2, -a - Math.PI / 2, new THREE.Vector3(0.14, 0.18, 0.02), 0, side * 0.2));
      }
    }

    // ---- The bell's frame by the node: two great posts on stone feet, the beam, braces, a chain off it. Empty.
    {
      const a = frameAt;
      const cx = spot.x;
      const cz = spot.z;
      const g = ctx.ground(cx, cz);
      const yaw = -a;
      const ax = Math.cos(a + Math.PI / 2);
      const az = Math.sin(a + Math.PI / 2);
      const H = spot.H;
      const half = 1.05;
      for (const side of [-1, 1]) {
        const px = cx + ax * half * side;
        const pz = cz + az * half * side;
        kit.add(box, STONE[1] as string, place(px, g + 0.12, pz, yaw, new THREE.Vector3(0.6, 0.24, 0.6)));
        kit.add(box, WOOD[side > 0 ? 0 : 1] as string, place(px, g + H / 2, pz, yaw, new THREE.Vector3(0.28, H, 0.28)));
        // Braces to the ground both ways out from the post (in the plane across the beam).
        for (const f of [-1, 1]) kit.add(box, WOOD[2] as string, place(px + Math.cos(a) * 0.4 * f, g + 0.7, pz + Math.sin(a) * 0.4 * f, yaw + Math.PI / 2, new THREE.Vector3(0.12, 1.55, 0.12), -0.5 * f));
      }
      // The beam and the yoke's rail under it, from post to post (a box's z, turned onto the tangent).
      kit.add(box, WOOD[0] as string, place(cx, g + H + 0.12, cz, yaw, new THREE.Vector3(0.32, 0.3, half * 2 + 0.7)));
      kit.add(box, WOOD[1] as string, place(cx, g + H - 0.12, cz, yaw, new THREE.Vector3(0.18, 0.12, half * 2 - 0.2)));
      // Iron bands where the bell's yoke sat, and its chain hanging empty.
      for (const side of [-1, 1]) kit.add(box, IRON, place(cx + ax * 0.3 * side, g + H + 0.12, cz + az * 0.3 * side, yaw, new THREE.Vector3(0.36, 0.34, 0.06)));
      // A little roof of planks over the beam, as a belfry's: two pitches and a ridge.
      // (A box's x across the beam, its z along it; rolled about the beam to pitch each side down.)
      for (const f of [-1, 1]) kit.add(box, WOOD[1] as string, place(cx + Math.cos(a) * 0.27 * f, g + H + 0.47, cz + Math.sin(a) * 0.27 * f, yaw, new THREE.Vector3(0.7, 0.06, half * 2 + 1.0), 0, -0.62 * f));
      kit.add(box, WOOD[2] as string, place(cx, g + H + 0.7, cz, yaw, new THREE.Vector3(0.1, 0.1, half * 2 + 1.05)));
      // The yoke the bell hung from, still on the beam: a heavy curved timber, iron-strapped; under it nothing.
      kit.add(box, WOOD[2] as string, place(cx, g + H - 0.34, cz, yaw, new THREE.Vector3(0.34, 0.26, 1.15)));
      for (const f of [-1, 1]) {
        kit.add(box, WOOD[2] as string, place(cx + ax * 0.62 * f, g + H - 0.2, cz + az * 0.62 * f, yaw, new THREE.Vector3(0.3, 0.2, 0.22), 0.5 * f));
        kit.add(box, IRON, place(cx + ax * 0.4 * f, g + H - 0.34, cz + az * 0.4 * f, yaw, new THREE.Vector3(0.38, 0.3, 0.05)));
      }
      // A snapped rope off the yoke, and the bell's iron crown-bolt lying in the planks.
      for (let k = 0; k < 4; k++) kit.add(link, IRON, place(cx + ax * 0.1, g + H - 0.55 - k * 0.12, cz + az * 0.1, k * 1.57, 1, k % 2 ? Math.PI / 2 : 0));
      // A heap of rotted planks under it, where the old platform fell.
      for (let k = 0; k < 4; k++) kit.add(box, WOOD[k % 3] as string, place(cx + (k - 1.5) * 0.25, g + 0.05 + k * 0.03, cz + 0.2 * Math.sin(k), yaw + k * 0.5, new THREE.Vector3(0.9, 0.05, 0.16)));
    }

    // ---- The slope: the hide's folds across it, long low ridges off the roads, creased; plates of old scale here and there.
    if (!post) {
      const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
      const fold = new THREE.SphereGeometry(0.5, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2);
      const plate = new THREE.CylinderGeometry(0.5, 0.6, 0.12, 6);
      let k = 0;
      for (let q = -map.radius; q <= map.radius; q++) {
        for (let r = -map.radius; r <= map.radius; r++) {
          const d = (Math.abs(q) + Math.abs(r) + Math.abs(-q - r)) / 2;
          if (d < 2 || d > map.radius || onPath.has(`${q},${r}`)) continue;
          // Every third free hex or so, by a fixed scatter.
          const h = (q * 73856093) ^ (r * 19349663);
          if (((h >>> 0) % 4) !== 0) continue;
          const w = hexToWorld(q, r);
          const x = (w.x / 1000) * S + (((h >>> 4) % 7) - 3) * 0.06;
          const z = (w.y / 1000) * S + (((h >>> 8) % 7) - 3) * 0.06;
          const g = ctx.ground(x, z);
          // Across the slope: the folds run round the head (tangent to the node).
          const yaw = -Math.atan2(z, x);
          const len = 0.8 + ((h >>> 12) % 5) * 0.14;
          kit.add(fold, HIDE_HEX[k % 3] as string, place(x, g - 0.03, z, yaw, new THREE.Vector3(0.36, 0.12 + ((h >>> 16) % 3) * 0.04, len)));
          // A second, lower fold just downhill of it: the hide creases in pairs.
          const out = Math.atan2(z, x);
          kit.add(fold, HIDE_HEX[(k + 1) % 3] as string, place(x + Math.cos(out) * 0.3, g - 0.04, z + Math.sin(out) * 0.3, yaw, new THREE.Vector3(0.26, 0.08, len * 0.7)));
          if (k % 4 === 1) kit.add(plate, k % 2 ? '#56604c' : '#454f3e', place(x - 0.3, g + 0.04, z + 0.2, k, new THREE.Vector3(0.42, 1, 0.36), 0.12));
          if (k % 5 === 2) kit.add(rock, STONE[k % 3] as string, place(x + 0.35, g + 0.06, z - 0.25, k, 0.18));
          k++;
        }
      }
      fold.dispose();
      plate.dispose();
    }

    // ---- The cold hearth: a ring of stones, charcoal, a pot on its hook.
    if (post && clear(Math.cos(hearthAt) * 1.25 * S, Math.sin(hearthAt) * 1.25 * S)) {
      const a = hearthAt;
      const hx = Math.cos(a) * 1.25 * S;
      const hz = Math.sin(a) * 1.25 * S;
      const g = ctx.ground(hx, hz);
      for (let k = 0; k < 8; k++) {
        const t = (k / 8) * Math.PI * 2;
        kit.add(rock, STONE[k % 3] as string, place(hx + Math.cos(t) * 0.32, g + 0.07, hz + Math.sin(t) * 0.32, k, 0.16));
      }
      kit.add(cyl, '#1a1816', place(hx, g + 0.02, hz, 0, new THREE.Vector3(0.5, 0.03, 0.5)));
      for (let k = 0; k < 3; k++) kit.add(box, '#14110e', place(hx, g + 0.06, hz, k * 1.1, new THREE.Vector3(0.36, 0.05, 0.06)));
      kit.add(box, WOOD[2] as string, place(hx - 0.3, g + 0.42, hz, 0, new THREE.Vector3(0.04, 0.84, 0.04), 0, 0.3));
      kit.add(box, WOOD[2] as string, place(hx + 0.3, g + 0.42, hz, 0, new THREE.Vector3(0.04, 0.84, 0.04), 0, -0.3));
      kit.add(box, WOOD[1] as string, place(hx, g + 0.8, hz, 0, new THREE.Vector3(0.6, 0.04, 0.04)));
      kit.add(pot, IRON, place(hx, g + 0.5, hz, 0, new THREE.Vector3(0.3, 0.26, 0.3)));
    }

    // ---- The camp going up: two tents of dark oilcloth, the third still only its poles, a pile of packs.
    for (let k = 0; k < (post ? 3 : 0); k++) {
      const a = campAt + (k - 1) * 0.62;
      const tx = Math.cos(a) * 1.75 * S;
      const tz = Math.sin(a) * 1.75 * S;
      if (!clear(tx, tz)) continue;
      const g = ctx.ground(tx, tz);
      const yaw = -a + Math.PI / 2;
      if (k < 2) {
        kit.add(cone, CLOTH[k] as string, place(tx, g + 0.42, tz, yaw + Math.PI / 4, new THREE.Vector3(1.1, 0.84, 0.9)));
        kit.add(box, '#141a20', place(tx + Math.cos(a) * 0.05, g + 0.24, tz + Math.sin(a) * 0.05, yaw, new THREE.Vector3(0.18, 0.44, 0.02)));
      } else {
        for (const side of [-1, 1]) kit.add(box, WOOD[1] as string, place(tx + Math.cos(yaw) * 0.4 * side, g + 0.4, tz - Math.sin(yaw) * 0.4 * side, yaw, new THREE.Vector3(0.05, 0.8, 0.05)));
        kit.add(box, WOOD[0] as string, place(tx, g + 0.8, tz, yaw + Math.PI / 2, new THREE.Vector3(0.05, 0.05, 0.9)));
        kit.add(box, CLOTH[2] as string, place(tx + Math.cos(a) * 0.2, g + 0.45, tz + Math.sin(a) * 0.2, yaw, new THREE.Vector3(0.88, 0.7, 0.02), 0.5));
        for (let r = 0; r < 3; r++) kit.add(cyl, CLOTH[r % 3] as string, place(tx - Math.cos(a) * 0.45 + r * 0.12, g + 0.08, tz - Math.sin(a) * 0.45, r, new THREE.Vector3(0.18, 0.16, 0.18), Math.PI / 2));
      }
      this.camp.push(new THREE.Vector3(tx, g + 0.3, tz));
    }

    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.05, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'post-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }

    // ---- The eye, beyond the board where the camera looks (out past the first road's gate, as the fight opens framed
    // on it): a closed lid the size of a valley, scaled, its seam lit, its face to the board.
    const g0 = map.sectors[0]?.gate ?? { q: 1, r: 0 };
    const gw = hexToWorld(g0.q, g0.r);
    const gl = Math.hypot(gw.x, gw.y) || 1;
    const dx = gw.x / gl;
    const dz = gw.y / gl;
    const d = (map.radius + 2) * S;
    const ex = dx * d;
    const ez = dz * d;
    // Seated on the hills out there (the outskirts rise towards the edge of the world).
    const gy = ctx.ground(ex, ez) - 0.6;
    const yaw = Math.atan2(-dx, -dz);
    // How big: it has to rise into the top of the board's view from out past the edge.
    const E = 1.5;
    const lid = new Kit();
    const dome = new THREE.SphereGeometry(1, 28, 14, 0, Math.PI * 2, 0, Math.PI / 2);
    const scale = new THREE.CylinderGeometry(0.5, 0.6, 0.2, 6);
    // The brow's ridge behind, the lid in front (facing the board).
    lid.add(dome, LID_DARK, place(ex + dx * 3.3 * E, gy + 0.5, ez + dz * 3.3 * E, yaw, new THREE.Vector3(18 * E, 9.2 * E, 4.6 * E)));
    lid.add(dome, LID, place(ex, gy, ez, yaw, new THREE.Vector3(15 * E, 7.4 * E, 6.5 * E)));
    const toWorld = (lx: number, lz: number): [number, number] => [ex + Math.cos(yaw) * lx + Math.sin(yaw) * lz, ez - Math.sin(yaw) * lx + Math.cos(yaw) * lz];
    // Scales on the lid, in rows that follow its curve.
    for (let row = 0; row < 4; row++) {
      for (let k = -6; k <= 6; k++) {
        const u = k / 7;
        const v = 0.25 + row * 0.18;
        const cu = Math.sqrt(Math.max(0, 1 - u * u));
        const lx = u * 14.2 * E;
        const ly = Math.sin(v * Math.PI * 0.5) * 7.2 * E * cu;
        const lz = 6.2 * E * Math.cos(v * Math.PI * 0.5) * cu;
        const [wx, wz] = toWorld(lx, lz);
        lid.add(scale, (row + k) % 2 ? '#56604c' : '#454f3e', place(wx, gy + ly, wz, yaw + k, new THREE.Vector3(1.6 * E, 1, 1.3 * E), -0.9 + v));
      }
    }
    // Lifted a little out of the night (moonlit), and not lost in the board's fog.
    const lm = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0, flatShading: true, fog: false, emissive: '#1a2216', emissiveIntensity: 1 });
    this.mats.push(lm);
    const lmesh = lid.build(lm, 'eyelid');
    if (lmesh) {
      lmesh.frustumCulled = false;
      this.geos.push(lmesh.geometry);
      this.group.add(lmesh);
    }
    // The seam where the lid meets the eye: a thin curve of light, faint, breathing.
    const pts: THREE.Vector3[] = [];
    // Across the lid's face a third of the way up, as an eye shut from the front.
    for (let k = 0; k <= 24; k++) {
      const u = ((k / 24) * 2 - 1) * 0.9;
      const lift = 0.3 - 0.06 * u * u;
      const [wx, wz] = toWorld(u * 15 * E, 6.5 * E * Math.sqrt(Math.max(0, 1 - u * u - lift * lift)) * 1.02);
      pts.push(new THREE.Vector3(wx, gy + 7.4 * E * lift, wz));
    }
    const seamG = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 48, 0.22, 5, false);
    this.seamM = new THREE.MeshBasicMaterial({ color: SLIT, transparent: true, opacity: 0.8, fog: false, depthWrite: false });
    this.geos.push(seamG);
    this.mats.push(this.seamM);
    const seam = new THREE.Mesh(seamG, this.seamM);
    seam.name = 'eyelid-seam';
    seam.frustumCulled = false;
    this.group.add(seam);
    const [mx, mz] = toWorld(0, 6.4 * E);
    this.eye.set(mx, gy + 2.2 * E, mz);
    this.group.add(this.glow.mesh);
    for (const g of [box, cyl, cone, rock, link, pot, dome, scale]) g.dispose();
    this.sky = captureSky(ctx);
    if (night) paintHour(ctx, this.sky, 1, 0);
  }

  /** The board's grass to the head's hide (creased, grey-green), its roads worn paler; no trees on a titan's head. */
  apply(terrain: TerrainBuild): void {
    const c = new THREE.Color();
    const p = new THREE.Vector3();
    const tint = (mesh: THREE.Mesh, road: boolean): void => {
      const geo = mesh.geometry;
      const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
      if (!col || !pos) return;
      mesh.updateMatrixWorld(true);
      for (let i = 0; i < pos.count; i++) {
        p.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const n = 0.5 + 0.5 * Math.sin(p.x * 1.3 + p.z * 0.7) * Math.cos(p.z * 1.1 - p.x * 0.4);
        if (road) c.copy(HIDE_ROAD).multiplyScalar(0.8 + lum * 0.6);
        else c.copy(HIDE[Math.min(2, Math.floor(n * 3))] as THREE.Color).multiplyScalar(0.75 + lum * 0.9);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    };
    tint(terrain.mesh, false);
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground') tint(o as THREE.Mesh, false);
      else if (o.name === 'roads') tint(o as THREE.Mesh, true);
      // Trees, bushes and grass don't grow on the titan's hide.
      if ((o as THREE.InstancedMesh).isInstancedMesh && (o.name === 'trunks' || o.name === 'cones' || o.name === 'blobs' || o.name === 'tufts')) o.visible = false;
    });
  }

  tick(s: BattleState): void {
    this.fx.tick(s);
    // A thread of smoke from the camp's lamp; the old hearth is cold.
    const c = this.camp[0];
    if (c && s.tick % (this.ctx.low ? 12 : 6) === 0) this.ctx.vfx.burst(c, { n: 1, color: '#6a6e76', speed: 0.004, up: 0.03, size: 0.2, life: 40, smoke: true, spread: 0.05 });
  }

  draw(_s: BattleState): void {
    // The lid's seam: faint, and now and then it brightens, as if the eye under it moved.
    const flick = Math.max(0, Math.sin(this.now * 0.37)) ** 12;
    this.seamM.opacity = Math.min(1, 0.6 + 0.15 * Math.sin(this.now * 0.9) + 0.35 * flick);
    this.glow.begin();
    for (let i = 0; i < 2; i++) {
      const p = this.camp[i];
      if (p) this.glow.add(p.x, p.y + 0.1, p.z, WARM, 0.9, 0.22 + 0.05 * Math.sin(this.now * 3 + i));
    }
    this.glow.add(this.eye.x, this.eye.y, this.eye.z, SLIT, 13, 0.06 + 0.2 * flick);
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
